import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { routes } from '@platform/core/lib/routes';
import type { IPartyCalendarProps } from './IPartyCalendarProps';
import type { IPartyEvent } from './IPartyEvent';
import { PartyCalendarMonth } from './PartyCalendarMonth';
import { PartyCalendarUpcoming } from './PartyCalendarUpcoming';
import { localIso, monthKeyOf, upcomingEvents } from './partyCalendarModel';
import './pcal.css';

/** Параметр адреси з місяцем календаря (YYYY-MM; без нього — поточний місяць, як «Сьогодні» прототипу). */
const MONTH_PARAM = 'month';

/**
 * Календар партії (прототип v4 pcal): місяць (з адреси `?month=`) і «Найближчі події» поруч, з перенесенням на вузьких екранах. Вимкнені
 * типи легенди ховають свої події і в сітці, і в кількості місяця, і в найближчих. Подія відкриває свій запис.
 */
export function PartyCalendar({ data, lang }: IPartyCalendarProps) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [off, setOff] = useState<Record<string, boolean>>({});
  const now = new Date();
  const today = localIso(now);
  const month = monthKeyOf(params.get(MONTH_PARAM), now);
  const visible = useMemo(() => data.events.filter((e) => !off[e.type.key]), [data.events, off]);
  const setMonth = (next: string | null) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (next) n.set(MONTH_PARAM, next);
    else n.delete(MONTH_PARAM);
    return n;
  }, { replace: true });
  const open = (e: IPartyEvent) => navigate(routes.record(e.type.moduleKey, e.type.key, e.record.id));
  return (
    <div className="pcal">
      <PartyCalendarMonth month={month} today={today} lang={lang} types={data.types} events={data.events} visible={visible} off={off}
        onToggle={(key) => setOff((o) => ({ ...o, [key]: !o[key] }))} onMonth={setMonth} onOpen={open} />
      <PartyCalendarUpcoming events={upcomingEvents(visible, today)} lang={lang} onOpen={open} />
    </div>
  );
}
